import React, { useState, useEffect } from 'react';
import CommentThread, { SystemThreadEvent } from './CommentThread';
import { useEntityComments } from './useEntityComments';
import { CommentEntityType, commentsAreClosedTo } from '../../services/CommentsService';
import { useRole } from '../../hooks/useRole';
import { loadStatusHistory, StatusHistoryEntityType, EntityStatusChange } from '../../services/StatusHistoryService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * A record's activity block: its comment thread and its status history, in
 * one chronological feed.
 *
 * Every page that already had a thread repeated the identical
 * `<section className="detail-history"><hr/><CommentThread …/></section>`
 * wrapper plus the same four lines of post/edit/delete wiring, and the two
 * pages with a history log each hand-rolled their own status-line rendering.
 * That's the drift this component removes: adding activity to a record is
 * now one element with two props, and a change to how a status change reads
 * lands on every page at once.
 *
 * Renders nothing until the record has a real id — an unsaved form has
 * nothing to attach a comment to.
 */
interface RecordActivityProps {
  /** Which entity's comment thread to load. */
  entityType: CommentEntityType;
  /** The saved record's id; undefined while the record is still new. */
  entityId?: string;
  /** Which entity's status history to load; omit for a record with no status. */
  historyType?: StatusHistoryEntityType;
  /** Display names for this entity's raw status values (e.g. 'pending_confirmation'). */
  statusLabels?: Record<string, string>;
  /** History entries this page already has in hand (e.g. procurement's own
   *  per-entity status_history payload), merged in alongside the generic ones. */
  extraEvents?: SystemThreadEvent[];
  label?: string;
  /** Optional section heading above the feed, for the pages that already
   *  titled their thread ("Comments", "Team Comments"). Rendered here rather
   *  than left at the call site so the heading markup can't drift per page. */
  heading?: string;
  /** Composer placeholder and empty-state wording, for a record whose feed
   *  isn't called "comments" (an event's Notes tab calls them updates). */
  placeholder?: string;
  emptyText?: string;
  /** Set on a surface with no sidebar beside it — the default width cap
   *  reserves room for a 320px column that isn't there. */
  fullWidth?: boolean;
}

/**
 * Source values that carry no information worth showing. 'manual' and
 * 'system' are both just "nothing automated named itself" — printing
 * "· manual" next to a transition a person obviously made is noise, and the
 * two feeds disagreed about which of the two words to suppress before this
 * was one list.
 */
const UNINFORMATIVE_SOURCES = new Set(['manual', 'system']);

/** 'pending_confirmation' → 'Pending confirmation'. */
export function humaniseStatus(raw: string, labels?: Record<string, string>): string {
  if (labels?.[raw]) return labels[raw];
  const spaced = raw.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * One status change as it reads in the feed. Kept here rather than at each
 * call site so "Draft → Complete · colala poll" looks the same everywhere.
 * The actor gets the same treatment as a comment's author, so a transition
 * reads as "who did this" rather than as an unattributed log line.
 */
export function statusChangeEvent(entry: EntityStatusChange, labels?: Record<string, string>): SystemThreadEvent {
  return {
    id: `status-${entry.id}`,
    timestampISO: entry.changedAtISO,
    actor: entry.actor,
    content: (
      <>
        {entry.fromStatus && (
          <>
            {humaniseStatus(entry.fromStatus, labels)}
            {' → '}
          </>
        )}
        <strong>{humaniseStatus(entry.toStatus, labels)}</strong>
        {entry.source && !UNINFORMATIVE_SOURCES.has(entry.source) ? ` · ${entry.source.replace(/_/g, ' ')}` : ''}
        {entry.notes ? ` — ${entry.notes}` : ''}
      </>
    ),
  };
}

const RecordActivity: React.FC<RecordActivityProps> = ({
  entityType,
  entityId,
  historyType,
  statusLabels,
  extraEvents,
  label = '',
  heading,
  placeholder,
  emptyText,
  fullWidth = false,
}) => {
  const { user } = useAuth();
  const { canViewFinancials } = useRole();
  const { comments, handlePost, handleDelete, handleEdit } = useEntityComments(entityType, entityId);
  const [history, setHistory] = useState<EntityStatusChange[]>([]);

  useEffect(() => {
    if (!historyType || !entityId) { setHistory([]); return; }
    // History is supplementary to the record itself — a failure here must not
    // take the page down, same as the comments load.
    loadStatusHistory(historyType, entityId).then(setHistory).catch(() => { /* noop */ });
  }, [historyType, entityId]);

  if (!entityId) return null;
  // A thread about money is closed to anyone who may not see money (the API answers 403 anyway).
  if (commentsAreClosedTo(entityType, canViewFinancials)) return null;

  const systemEvents = [
    ...history.map(h => statusChangeEvent(h, statusLabels)),
    ...(extraEvents ?? []),
  ];

  return (
    <section className={`detail-history${fullWidth ? ' detail-history--full' : ''}`}>
      <hr className="poe-divider" />
      {heading && <h3 className="poe-sub-heading">{heading}</h3>}
      <CommentThread
        label={label}
        comments={comments}
        systemEvents={systemEvents}
        onPost={handlePost}
        onDelete={handleDelete}
        onEdit={handleEdit}
        currentUserEmail={user?.email}
        {...(placeholder ? { placeholder } : {})}
        {...(emptyText ? { emptyText } : {})}
      />
    </section>
  );
};

export default RecordActivity;
